import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, FileText, Image as ImageIcon, Loader2, Sparkles, Upload } from "lucide-react";
import {
  FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS,
  FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS,
  hasAllEvtxSections,
  type FogbugzFormulatorAttachmentInfo,
  type FogbugzFormulatorToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { eventLogValidationError } from "@/components/fogbugz/event-log-upload";
import { ScreenshotPaste } from "@/components/fogbugz/screenshot-paste";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import {
  fogbugzToolboxInterviewKeys,
  useToolboxEvtxDigest,
  useToolboxInterviewAttachments,
} from "@/hooks/queries/use-fogbugz-toolbox-interview";
import { useClaudeRunOutcome } from "@/lib/toolbox/claude-signals";
import { attachmentName, fogbugzFormulatorMessage } from "@/lib/toolbox/fogbugz-formulator";
import { cn } from "@/lib/utils";

/** Human-readable size. Pure; exported for tests. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

async function bytesOf(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

export interface AttachmentsProps {
  bridge: FogbugzFormulatorToolsetBridge;
  sessionId: string;
  /** The linked ADO project, for plain error messages. */
  adoProject: string;
  /** Summaries the person accepted, by event-log file name. */
  summaries: Readonly<Record<string, string>>;
  onSummariesChange: (next: Record<string, string>) => void;
  /** Hide the add controls (the review step lists what goes with the case). */
  readOnly?: boolean;
}

/**
 * Screenshots and event logs of a Toolbox FogBugz Formulator interview (epic
 * PI-SPEC-SW-E309, ER-1665 decisions 3 and 4).
 *
 * Files stay in the Toolbox's private session folder on this computer until
 * submit (`addImage`, `addEventLog`); the list is read back from there
 * (`listAttachments`), so it survives a reload or an app restart. The next
 * interview turn's Claude reads the screenshots and each event log's
 * deterministic digest (`POST /toolbox-interview/evtx-digest`, no AI).
 * "Summarize with my Claude" runs the existing EVTX summary skill on the
 * person's own Claude (`summarizeEventLog`); the four sections are shown for
 * review and go with the case only once the person chooses "Use this summary".
 */
export function Attachments({ bridge, sessionId, adoProject, summaries, onSummariesChange, readOnly = false }: AttachmentsProps) {
  const queryClient = useQueryClient();
  const reportClaudeRun = useClaudeRunOutcome();
  const list = useToolboxInterviewAttachments(bridge, sessionId);
  const digest = useToolboxEvtxDigest();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [summarizing, setSummarizing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; sections: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  const pendingImagesRef = useRef<string[]>([]);

  const attachments: FogbugzFormulatorAttachmentInfo[] = list.data ?? [];
  const images = attachments.filter((item) => item.kind === "image");
  const eventLogs = attachments.filter((item) => item.kind === "event-log");
  const names = attachments.map((item) => item.name);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.attachments(sessionId) });
  };

  const refusal = (code: string, message: string) => fogbugzFormulatorMessage(code, { adoProject, engineMessage: message }).text;

  const addImage = async (file: File) => {
    // Names (and slots under the cap) taken by screenshots still on their way
    // in: two quick pastes are both `image.png`, and the list above is only
    // as new as its last read. A name is held until the list has it.
    const pending = pendingImagesRef.current;
    if (images.length + pending.length >= FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS) {
      throw new Error(refusal("TOO_MANY_IMAGES", ""));
    }
    const ext = file.type === "image/jpeg" ? ".jpg" : file.type === "image/webp" ? ".webp" : file.type === "image/gif" ? ".gif" : ".png";
    const name = attachmentName(file.name || `screenshot${ext}`, [...names, ...pending], `screenshot${ext}`);
    pending.push(name);
    try {
      const result = await bridge.addImage({ sessionId, name, bytes: await bytesOf(file) });
      await queryClient.invalidateQueries({ queryKey: fogbugzToolboxInterviewKeys.attachments(sessionId) });
      if (!result.ok) throw new Error(refusal(result.errorCode, result.message));
    } finally {
      pendingImagesRef.current = pendingImagesRef.current.filter((held) => held !== name);
    }
  };

  const addEventLogs = async (files: File[]) => {
    if (files.length === 0) return;
    if (busyRef.current) {
      setError("Wait for the current event log to finish.");
      return;
    }
    busyRef.current = true;
    setError(null);
    let count = eventLogs.length;
    const taken = [...names];
    try {
      for (const file of files) {
        const problem = eventLogValidationError(file, count);
        if (problem) {
          setError(problem);
          continue;
        }
        setAdding(file.name);
        try {
          const response = await digest.mutateAsync(file);
          const name = attachmentName(file.name, taken, "event-log.evtx");
          const result = await bridge.addEventLog({
            sessionId,
            name,
            bytes: await bytesOf(file),
            digestMarkdown: response.data.digestMarkdown,
          });
          if (!result.ok) {
            setError(refusal(result.errorCode, result.message));
            continue;
          }
          taken.push(name);
          count += 1;
        } catch (addError) {
          setError(addError instanceof Error && addError.message ? addError.message : `Could not add ${file.name}.`);
        }
      }
    } finally {
      busyRef.current = false;
      setAdding(null);
      refresh();
    }
  };

  const summarize = async (name: string) => {
    if (summarizing !== null) return;
    setSummarizing(name);
    setError(null);
    setDraft(null);
    try {
      const result = await bridge.summarizeEventLog({ sessionId, name });
      reportClaudeRun(result.ok ? { ok: true } : { ok: false, errorCode: result.errorCode });
      if (!result.ok) {
        setError(refusal(result.errorCode, result.message));
        return;
      }
      // Untrusted model output: all four sections, in order, or nothing to review.
      if (!hasAllEvtxSections(result.sections)) {
        setError("Claude finished without a complete summary (all four sections). Summarize again.");
        return;
      }
      setDraft({ name, sections: result.sections });
    } catch {
      setError("SpecTree Toolbox could not run Claude for this summary. Try again.");
    } finally {
      setSummarizing(null);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="fogbugz-formulator-attachments">
      {!readOnly && (
        <div className="grid gap-3 md:grid-cols-2 md:items-start">
          <div>
            <h3 className="mb-2 text-sm font-medium">Screenshots</h3>
            <ScreenshotPaste sessionId={sessionId} onFile={addImage} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-medium">Event logs (.evtx)</h3>
            <div
              className="rounded-lg border-2 border-dashed border-white/[0.08] px-4 py-3 text-center text-sm text-muted-foreground hover:border-white/[0.2]"
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                void addEventLogs(Array.from(event.dataTransfer.files));
              }}
              data-testid="fogbugz-formulator-evtx-drop"
            >
              <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <Upload className="h-4 w-4" aria-hidden="true" />
                <span>
                  <strong className="text-foreground">Drop an .evtx event log</strong> or{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline disabled:opacity-50"
                    disabled={adding !== null || eventLogs.length >= FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    browse
                  </button>
                </span>
                <span className="text-xs">Up to {FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS} logs, 10 MB each</span>
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".evtx"
                multiple
                className="hidden"
                data-testid="fogbugz-formulator-evtx-input"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = "";
                  void addEventLogs(files);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {adding && (
        <p className="flex items-center gap-2 text-xs text-primary" role="status">
          <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          Adding <span className="text-muted-foreground">{adding}</span>
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
          data-testid="fogbugz-formulator-attachment-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {list.isError && (
        <p className="text-xs text-muted-foreground" role="status">
          The files kept for this report could not be listed on this computer.
        </p>
      )}

      {attachments.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Files for this report" data-testid="fogbugz-formulator-attachment-list">
          {[...images, ...eventLogs].map((item) => {
            const accepted = item.kind === "event-log" ? summaries[item.name] : undefined;
            return (
              <li
                key={`${item.kind}-${item.name}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.08] p-2.5 text-sm"
                data-testid="fogbugz-formulator-attachment"
              >
                {item.kind === "image" ? (
                  <ImageIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 truncate font-medium" title={item.name}>
                  {item.name}
                </span>
                <span className="text-xs text-muted-foreground">{formatBytes(item.sizeBytes)}</span>
                {item.kind === "event-log" && accepted !== undefined && <Chip tone="ok">Summary reviewed</Chip>}
                {item.kind === "event-log" && !readOnly && (
                  <>
                    {accepted !== undefined ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const next = { ...summaries };
                          Reflect.deleteProperty(next, item.name);
                          onSummariesChange(next);
                        }}
                      >
                        Remove summary
                      </Button>
                    ) : summarizing === item.name ? (
                      <span className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
                        <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        Claude is writing the summary
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={summarizing !== null}
                        aria-label={`Summarize ${item.name} with my Claude`}
                        onClick={() => {
                          void summarize(item.name);
                        }}
                      >
                        <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Summarize with my Claude
                      </Button>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {draft && (
        <section
          className="flex flex-col gap-3 rounded-lg border border-white/[0.08] p-3"
          aria-labelledby="fogbugz-formulator-summary-review"
          data-testid="fogbugz-formulator-summary-review"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="fogbugz-formulator-summary-review" className="text-sm font-semibold">
              Review the summary of {draft.name}
            </h3>
            <Chip tone="cur">Written by your Claude</Chip>
          </div>
          <p className="text-xs text-muted-foreground">
            Check it against the event log. It is attached to the case with the log only if you use it.
          </p>
          <div
            className={cn(
              "max-h-[40vh] overflow-y-auto rounded-md border border-white/[0.08] bg-white/[0.02] p-3 text-sm",
            )}
          >
            <MarkdownRenderer content={draft.sections} disableLinks />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onSummariesChange({ ...summaries, [draft.name]: draft.sections });
                setDraft(null);
              }}
            >
              Use this summary
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setDraft(null);
              }}
            >
              Discard
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
